import { createContext, useContext, useMemo } from 'react';

/**
 * Context para dados normalizados da página de Professionals
 * Mantém cache local de places, services e payments
 * que são compartilhados entre todos os professionals
 * 
 * Estrutura: professionals[].places[].services[]
 * Padrão: vínculos aninhados (igual a Places)
 */
const ProfessionalsDataContext = createContext(null);

export function ProfessionalsDataProvider({ children, data }) {
  // Cache das listas auxiliares (imutável durante a sessão da página)
  const professionals = useMemo(() => data?.professionals || [], [data?.professionals]);
  const places = useMemo(() => data?.places || [], [data?.places]);
  const services = useMemo(() => data?.services || [], [data?.services]);
  const payments = useMemo(() => data?.payments || [], [data?.payments]);

  // Criar Maps para lookup O(1) por ID
  const placesMap = useMemo(() => {
    const map = new Map();
    places.forEach(place => map.set(place.id, place));
    return map;
  }, [places]);

  const servicesMap = useMemo(() => {
    const map = new Map();
    services.forEach(svc => map.set(svc.id, svc));
    return map;
  }, [services]);

  const paymentsMap = useMemo(() => {
    const map = new Map();
    payments.forEach(pay => map.set(pay.id, pay));
    return map;
  }, [payments]);

  // Helper functions para lookup
  const getPlace = (id) => placesMap.get(id);
  const getService = (id) => servicesMap.get(id);
  const getPayment = (id) => paymentsMap.get(id);

  // Calcular estatísticas por professional
  const getProfessionalStats = (professional) => {
    const placesCount = professional.places?.length || 0;
    const servicesCount = professional.places?.reduce((total, placeLink) => {
      return total + (placeLink.services?.length || 0);
    }, 0) || 0;

    return {
      placesCount,
      servicesCount,
      placeNames: professional.places?.map(pl => getPlace(pl.placeId)?.name).filter(Boolean) || [],
    };
  };

  const value = {
    // Listas originais (normalizadas)
    professionals,
    places,
    services,
    payments,
    
    // Maps para lookup rápido
    placesMap,
    servicesMap,
    paymentsMap,
    
    // Helper functions
    getPlace,
    getService,
    getPayment,
    getProfessionalStats,
  };

  return (
    <ProfessionalsDataContext.Provider value={value}>
      {children}
    </ProfessionalsDataContext.Provider>
  );
}

/**
 * Hook para acessar dados normalizados de Professionals
 * Fornece acesso direto às listas e funções de lookup
 */
export function useProfessionalsData() {
  const context = useContext(ProfessionalsDataContext);
  if (!context) {
    throw new Error('useProfessionalsData deve ser usado dentro de ProfessionalsDataProvider');
  }
  return context;
}

/**
 * Hook para acessar um place específico por ID
 */
export function usePlace(placeId) {
  const { getPlace } = useProfessionalsData();
  return useMemo(() => getPlace(placeId), [placeId, getPlace]);
}

/**
 * Hook para acessar um service específico por ID
 */
export function useService(serviceId) {
  const { getService } = useProfessionalsData();
  return useMemo(() => getService(serviceId), [serviceId, getService]);
}

/**
 * Hook para acessar um payment específico por ID
 */
export function usePayment(paymentId) {
  const { getPayment } = useProfessionalsData();
  return useMemo(() => getPayment(paymentId), [paymentId, getPayment]);
}
